"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, FlaskConical, Gauge, Microscope, SlidersHorizontal as Sliders } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
interface ProcessStep {
  number: string;
  title: string;
  subtitle: string;
  description: string;
  deliverables: string;
  iconName: "microscope" | "sliders" | "flask" | "gauge";
}
const steps: ProcessStep[] = [{
  number: "01",
  title: "Sample Assessment & Optical Evaluation",
  subtitle: "Feasibility & Spectral Characterisation",
  description: "Initial feasibility screening of your sample matrices, evaluating optical properties, absorbance characteristics, and spectral suitability under standard operating constraints.",
  deliverables: "Optical evaluation summary, feasibility data & instrument compatibility matrix",
  iconName: "microscope"
}, {
  number: "02",
  title: "Method Design & Parameter Definition",
  subtitle: "Calibration & Quantitative Models",
  description: "Custom calibration model development, spectral pre-processing selection, and parameter boundary definition tailored to your active ingredients and formulation matrix.",
  deliverables: "Preliminary calibration models, wavelength optimization & chemometric baselines",
  iconName: "sliders"
}, {
  number: "03",
  title: "Protocol Definition & Robustness Testing",
  subtitle: "Analytical Validation & Stress Testing",
  description: "Rigorous stress testing across environmental variances, sample batch heterogeneity, and operator transitions in compliance with ICH Q2(R1) and regulatory expectations.",
  deliverables: "Method validation report, robustness data & precision limit documentation",
  iconName: "flask"
}, {
  number: "04",
  title: "Integration & Deployment Planning",
  subtitle: "On-Site Commissioning & Qualification",
  description: "Seamless transfer into your QA/QC or in-line production workflow, backed by full qualification protocols (IQ/OQ), user training, and long-term lifecycle support.",
  deliverables: "Turnkey SOP packages, IQ/OQ execution records & operator training sign-offs",
  iconName: "gauge"
}];
export default function ProcessGrid() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (iconName: ProcessStep["iconName"]) => {
    switch (iconName) {
      case "microscope":
        return <Microscope className="size-5 text-primary" />;
      case "sliders":
        return <Sliders className="size-5 text-primary" />;
      case "flask":
        return <FlaskConical className="size-5 text-primary" />;
      case "gauge":
        return <Gauge className="size-5 text-primary" />;
    }
  };
  return <section id="processgrid" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 border-b border-border">
      <div className="page-container">
        {/* Header Block */}
        <div className="max-w-3xl mb-14 lg:mb-20">
          <Badge variant="outline" className="mb-4 bg-muted/60 text-foreground border-border font-medium">
            Methodology & Implementation
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground leading-[1.15]">
            A proven 4-stage process for analytical certainty.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            From initial optical evaluation through on-site qualification, our structured workflow minimizes technical risk and accelerates deployment in regulated laboratories.
          </p>
        </div>

        {/* 4-Stage Process Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => <motion.div key={step.number} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: shouldReduceMotion ? 0 : index * 0.1
        }} className="h-full flex">
              <Card className="flex flex-col justify-between w-full bg-muted/40 border border-border hover:border-primary/60 hover:bg-card transition-all duration-300 rounded-xl shadow-xs group">
                <CardHeader className="p-6 pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-3xl font-default font-bold text-foreground/40 group-hover:text-primary transition-colors duration-300">
                      {step.number}
                    </span>
                    <div className="size-10 rounded-lg bg-background border border-border flex items-center justify-center group-hover:border-primary/40 group-hover:bg-primary/5 transition-all duration-300 shadow-2xs">
                      {getIcon(step.iconName)}
                    </div>
                  </div>
                  <CardTitle className="text-xl font-default font-bold text-foreground leading-snug group-hover:text-primary transition-colors duration-300">
                    {step.title}
                  </CardTitle>
                  <CardDescription className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mt-1">
                    {step.subtitle}
                  </CardDescription>
                </CardHeader>

                <CardContent className="p-6 pt-2 flex-1 flex flex-col justify-between gap-6">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>

                  <div className="pt-4 border-t border-border/80">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <p className="text-xs text-foreground/80 leading-normal font-medium">
                        {step.deliverables}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>

        {/* Bottom CTA bar */}
        <div className="mt-14 p-6 sm:p-8 rounded-xl bg-muted/50 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h3 className="text-lg font-default font-bold text-foreground">
              Ready to evaluate your sample matrix?
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Speak with our analytical specialists or request a targeted feasibility study.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <Button asChild variant="default" className="w-full sm:w-auto">
              <Link to="/FeasibilityStudiesPage" className="inline-flex items-center gap-2">
                <span>Start Feasibility Study</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="w-full sm:w-auto">
              <Link to="/DiscussYourApplicationPage">
                Discuss Your Application
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}